/**
 * RWBR — the driver name pool.
 *
 * HOWARD, 17 Aug 2026, verbatim, is the specification for this whole file:
 *
 *   "are the fictional names the same every time? I dont like that. Let the ai
 *    make a batch first and then changing the seed only moves around those AI
 *    created names and doesnt remake more. Only the first loading when someone
 *    visits the site without a career going."
 *
 * Four separate requirements live in that sentence and each one is load-bearing:
 *
 *   1. THE NAMES MUST NOT BE THE SAME EVERY TIME. Until now they were: 60 first
 *      names × 82 surnames in `field.ts`, dealt by a seeded RNG. Same seed, same
 *      grid, forever — and worse, the same *vocabulary* across every league
 *      anyone will ever play, so two players a thousand miles apart both get a
 *      Wingo and a Ledbetter.
 *
 *   2. THE AI MAKES A BATCH FIRST. One call, up front, producing a POOL.
 *
 *   3. RE-SEEDING MOVES NAMES AROUND AND NEVER MAKES MORE. Changing the race
 *      seed permutes the pool. It does not call the model, does not add a name,
 *      and cannot produce a name the model did not write. This is why the pool
 *      is a list of WHOLE names and never a first-name list plus a surname list:
 *      recombining halves would invent "Otis Coffey" out of two names the model
 *      wrote, which is remaking more.
 *
 *   4. ONLY ON A FIRST VISIT WITH NO CAREER RUNNING. A career already under way
 *      has drivers with statistics, winnings and a history attached to their
 *      names. Regenerating the vocabulary underneath a running career would
 *      rename the champion. So `ensureNamePool` is told whether a career exists
 *      and refuses to generate when one does.
 *
 * WHAT HAPPENS WHEN THE MODEL IS NOT THERE. Nothing stops. No key, no network,
 * a refused batch, a browser with storage switched off — every one of those
 * returns `source: 'builtin'` with a `status` that says which, and the caller
 * falls through to exactly the behaviour the game has always had: `buildField`
 * naming drivers from its own vocabulary. There is no path in this file that
 * throws, and no path that ends in a field with no names on it. The status is
 * returned so the interface can SAY so — "these are the built-in names, the
 * model was unreachable" — rather than quietly pretending.
 *
 * WHERE THE POOL LIVES. `localStorage`, under one key, as JSON. That is enough
 * for an anonymous visitor, which is the case Howard described ("someone visits
 * the site"), and it survives a reload, which is the whole point. A per-account
 * store belongs in Supabase alongside `careerStore.ts` and is NOT built here —
 * see the note on `POOL_STORAGE_KEY`.
 */

import { aiAccess, isNoModel } from './aiKey.ts';
import { makeRng } from '../domain/dice.ts';

// ---------------------------------------------------------------------------
// How big a pool
// ---------------------------------------------------------------------------

/**
 * How many names to ask the model for.
 *
 * THIS IS ARITHMETIC, NOT A ROUND NUMBER. Two published facts set it:
 *
 *   • `DEFAULT_ROSTER_SIZE` = 53. That is how many drivers hold a licence in a
 *     season (14 charter teams × 3, plus 11 independents), so 53 names are
 *     consumed the moment a league is founded.
 *
 *   • The winter churn is 13. `offseason.ts` D-032 demotes the five lowest-ranked
 *     drivers plus up to eight who missed the 75% finish threshold — "thirteen of
 *     fifty-three — a quarter of the league" — and `signRookies` licenses a new
 *     driver for every empty seat. So a career burns 13 fresh names a season.
 *
 * 53 + 13 × 20 = 313. Rounded up to 320, that is a TWENTY-SEASON career in which
 * no driver is ever named after a driver who came before him. Twenty seasons is
 * 720 races; if a league outlives that, the built-in vocabulary picks up the
 * tail and `NameAssignment.shortfall` says so out loud.
 *
 * Not larger, for two honest reasons: a single completion has to fit it, and a
 * model asked for a thousand distinct names in one breath starts repeating and
 * reaching for real drivers — which means a bigger ask buys fewer usable names,
 * not more. We measure the realised yield rather than assuming it; see
 * `scripts/names.ts`.
 */
export const POOL_TARGET_SIZE = 320;

/**
 * Below this, the batch is not worth persisting and we stay on the built-in
 * vocabulary. 53 is a single roster with zero headroom — a pool that cannot even
 * name one field is a worse experience than the list we already ship, because
 * the field would be half AI names and half built-in ones for no gain. 64 gives
 * a full roster plus a margin.
 */
export const POOL_MIN_SIZE = 64;

/** Hard ceiling on anything accepted from the wire, pool or request. */
export const POOL_MAX_SIZE = 1000;

/** Bump when the stored shape changes; an older record is discarded, not migrated. */
export const POOL_SCHEMA_VERSION = 1;

/**
 * ⚠ ANONYMOUS-VISITOR STORAGE, DELIBERATELY.
 *
 * `localStorage` is per-browser, not per-account: a player on a second device
 * gets a second pool, and clearing site data loses it. That is the right trade
 * for the case in the requirement — a visitor with no career and no account —
 * and it is the wrong trade for a signed-in player with an eleven-season league.
 *
 * A per-account store is coming and belongs beside `careerStore.ts` (browser
 * reads with the publishable key, writes through the Worker, which holds the
 * secret). When it lands, this key becomes the anonymous fallback and the
 * account pool wins; nothing else in this file needs to change, because
 * everything below takes its pool as an argument.
 */
export const POOL_STORAGE_KEY = 'rwbr.namePool.v1';

// ---------------------------------------------------------------------------
// What a pool is
// ---------------------------------------------------------------------------

export interface NamePool {
  version: number;
  /** Full names, whole, in the order the model wrote them. Never split. */
  names: string[];
  /** ISO 8601. Purely for the interface to say "generated on ...". */
  generatedAt: string;
  /** Which model wrote it, if the Worker told us. Never a key, never a prompt. */
  model: string | null;
  /** How many the model returned before validation, so the yield is inspectable. */
  offered: number;
  /** Why each rejected candidate was rejected. Kept: it is the audit trail. */
  rejected: RejectedName[];
}

export interface RejectedName {
  candidate: string;
  reason: RejectionReason;
}

export type RejectionReason =
  /** Not a string, empty, or too long/short to be a name. */
  | 'malformed'
  /** Not two or three capitalised words — outside the register we asked for. */
  | 'not-a-driver-name'
  /** Already in the batch (case-insensitive). */
  | 'duplicate'
  /** Matches the real-driver deny-list. */
  | 'real-driver';

/**
 * Why the pool is what it is. Every one of these is a sentence the interface can
 * show a player, which is the point — a fallback the user cannot see is
 * indistinguishable from a bug.
 */
export type PoolStatus =
  /** The model wrote a batch, it passed validation, it is now persisted. */
  | 'generated'
  /** A pool was already stored. The model was NOT called. This is the steady state. */
  | 'cached'
  /** A career is running. Requirement 4: we do not regenerate underneath it. */
  | 'skipped-career-in-progress'
  /** No network, upstream error, or the Worker said no. */
  | 'unavailable'
  /** D-EZ-100 — nobody's key may be spent (no key of their own, not the owner). Not a failure. */
  | 'no-model'
  /** The model answered and too little of it survived validation. */
  | 'rejected'
  /** The browser refused to store it (private mode, quota, storage disabled). */
  | 'storage-unavailable';

export interface EnsuredPool {
  /** Null means: use the built-in vocabulary, unchanged. */
  pool: NamePool | null;
  status: PoolStatus;
  /** One plain sentence, safe to show a player. Never contains a key or a prompt. */
  detail: string;
}

// ---------------------------------------------------------------------------
// Validation — a system prompt is a request, this is the check
// ---------------------------------------------------------------------------

/**
 * Surnames that belong to a real NASCAR driver strongly enough that ANY first
 * name in front of them reads as a real person. A "Wendell Earnhardt" is still
 * an Earnhardt.
 *
 * ⚠ THIS LIST IS APPLIED TO MODEL OUTPUT ONLY, AND IT IS STRICTER THAN THE
 * VOCABULARY WE ALREADY SHIP. `field.ts`'s SURNAMES contains `Sadler` and
 * `Bodine`, both of which are real Cup surnames (Elliott Sadler; Geoff, Brett
 * and Todd Bodine). That list is a hand-curated human artifact, it is not mine
 * to change, and it is flagged here rather than quietly worked around — its
 * owner should decide. The asymmetry is deliberate and defensible: a curated
 * list of eighty-two surnames was chosen by a person who can be asked why, and
 * an unbounded generator cannot. What I accept from the model is my problem.
 *
 * Kept SMALL on purpose. A deny-list is a backstop for the system prompt, not a
 * substitute for it, and every entry costs a fictional surname that a player
 * might reasonably have wanted. It covers the names a casual fan would recognise
 * instantly, which is the failure that matters: a player seeing "Dale Earnhardt"
 * in a fictional league stops believing the league.
 */
export const REAL_DRIVER_SURNAMES: readonly string[] = [
  'Allison', 'Almirola', 'Ambrose', 'Andretti', 'Bayne', 'Bell', 'Biffle', 'Blaney',
  'Bodine', 'Bowman', 'Bowyer', 'Buescher', 'Burton', 'Busch', 'Byron', 'Chastain',
  'Cindric', 'Dillon', 'Earnhardt', 'Edwards', 'Elliott', 'Foyt', 'Gordon', 'Gragson',
  'Hamlin', 'Harvick', 'Jarrett', 'Johnson', 'Kahne', 'Keselowski', 'Kenseth', 'Labonte',
  'Larson', 'Logano', 'Marlin', 'Martin', 'Mayfield', 'McMurray', 'Menard', 'Nemechek',
  'Newman', 'Pearson', 'Petty', 'Preece', 'Reddick', 'Rudd', 'Sadler', 'Schrader',
  'Skinner', 'Sorenson', 'Spencer', 'Stenhouse', 'Stewart', 'Truex', 'Vickers', 'Wallace',
  'Waltrip', 'Yarborough', 'Yeley',
];

/**
 * Full names that slip past the surname list because the surname alone is
 * ordinary. "Kyle Petty" is caught above; "Kurt Busch" is caught above; "Jimmie
 * Johnson" is caught above — these are the ones where the surname is common
 * enough that denying it wholesale would cost too much.
 */
export const REAL_DRIVER_NAMES: readonly string[] = [
  'Bobby Isaac', 'Buddy Baker', 'Cale Yarborough', 'Curtis Turner', 'Darrell Waltrip',
  'David Pearson', 'Fireball Roberts', 'Fred Lorenzen', 'Harry Gant', 'Herb Thomas',
  'Junior Johnson', 'Lee Roy Yarbrough', 'Marshall Teague', 'Neil Bonnett', 'Ned Jarrett',
  'Rex White', 'Richard Childress', 'Rusty Wallace', 'Tim Flock', 'Tiny Lund',
  'Alan Kulwicki', 'Dale Jarrett', 'Geoff Bodine', 'Ken Schrader', 'Kyle Larson',
  'Ricky Rudd', 'Sterling Marlin', 'Terry Labonte', 'Ward Burton', 'Chase Briscoe',
  'Joey Gase', 'Ryan Preece', 'Corey Lajoie', 'Michael McDowell', 'Erik Jones',
];

/**
 * The register we are asking for, expressed as a shape.
 *
 * Two or three capitalised words. Apostrophes and hyphens allowed inside a word
 * ("O'Dell", "Van-Zant"); a trailing period allowed for an initial-style middle
 * ("Bo R. Nabors"), which the published sets do contain. Digits, punctuation
 * runs, quotes, parentheses and anything that looks like a sentence are out —
 * those are the shapes a model produces when it decides to be helpful and
 * annotate its answer ("Otis Barkley (veteran)"), and every one of them would
 * end up painted on a car.
 */
const NAME_SHAPE = /^[A-Z][a-zA-Z'’-]{1,15}(?: [A-Z][a-zA-Z'’-]{0,15}\.?){1,2}$/;

export interface ValidatedBatch {
  accepted: string[];
  rejected: RejectedName[];
}

/**
 * Check a batch of candidate names. Pure, synchronous, no I/O — so the counting
 * harness and the tests can run it a thousand times without a network.
 *
 * ORDER IS PRESERVED. The model's own ordering is meaningless to us, but keeping
 * it means a given batch validates to the same list every time, which is what
 * makes the persisted pool reproducible from the audit trail.
 */
export function validateNameBatch(raw: unknown): ValidatedBatch {
  const accepted: string[] = [];
  const rejected: RejectedName[] = [];
  if (!Array.isArray(raw)) return { accepted, rejected };

  const denySurnames = new Set(REAL_DRIVER_SURNAMES.map((s) => s.toLowerCase()));
  const denyFull = new Set(REAL_DRIVER_NAMES.map((s) => s.toLowerCase()));
  const seen = new Set<string>();

  for (const entry of raw.slice(0, POOL_MAX_SIZE)) {
    if (typeof entry !== 'string') {
      rejected.push({ candidate: String(entry).slice(0, 40), reason: 'malformed' });
      continue;
    }
    // Collapse internal whitespace before anything else: "Otis  Barkley" and
    // "Otis\tBarkley" are the same name badly typed, not two names.
    const name = entry.replace(/\s+/g, ' ').trim();
    if (name.length === 0 || name.length > 48) {
      rejected.push({ candidate: entry.slice(0, 40), reason: 'malformed' });
      continue;
    }
    if (!NAME_SHAPE.test(name)) {
      rejected.push({ candidate: name, reason: 'not-a-driver-name' });
      continue;
    }
    const words = name.split(' ');
    const surname = words[words.length - 1].replace(/[.'’-]+$/, '').toLowerCase();
    if (denyFull.has(name.toLowerCase()) || denySurnames.has(surname)) {
      rejected.push({ candidate: name, reason: 'real-driver' });
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      rejected.push({ candidate: name, reason: 'duplicate' });
      continue;
    }
    seen.add(key);
    accepted.push(name);
  }

  return { accepted, rejected };
}

// ---------------------------------------------------------------------------
// Permutation — requirement 3
// ---------------------------------------------------------------------------

/**
 * Keeps the name stream off the card stream.
 *
 * `buildField` already derives its identity RNG as `seed ^ 0x9e3779b9` "so that
 * changing the name pools never moves a rating". This is a THIRD stream off the
 * same seed, distinct from both, so that a reshuffled pool cannot disturb either
 * the ratings or the car numbers. The constant is the fractional part of the
 * golden ratio's conjugate again, rotated — any fixed odd constant would do; the
 * requirement is only that it differ from the one `field.ts` uses.
 */
const NAME_STREAM_SALT = 0x5bf03635;

/**
 * Deal `count` names for a given seed WITHOUT GENERATING ANYTHING.
 *
 * This is requirement 3 in eleven lines. A partial Fisher–Yates over a copy of
 * the pool, driven by the seed:
 *
 *   • Every name it returns is a name already in the pool. There is no path by
 *     which a new name can appear — no concatenation, no recombination, no
 *     mutation, no call.
 *   • No name appears twice in one deal, because a shuffle draws without
 *     replacement. The collision rate inside a field is therefore exactly zero
 *     BY CONSTRUCTION rather than by retry, which is what `field.ts`'s
 *     `nextName` had to do with its cross product. `scripts/names.ts` counts it
 *     anyway — construction arguments have been wrong before.
 *   • A different seed produces a different arrangement AND a different subset,
 *     which is what "moves around" has to mean when the pool is bigger than the
 *     field. Two seeds share about `count² / pool.length` names, and that number
 *     is measured rather than asserted.
 *
 * Returns fewer than `count` only when the pool holds fewer than `count` names;
 * the caller reports the shortfall and the built-in vocabulary covers the tail.
 */
export function permuteForSeed(
  pool: readonly string[],
  seed: number,
  count: number,
): string[] {
  const n = pool.length;
  const take = Math.max(0, Math.min(Math.floor(count), n));
  if (take === 0) return [];

  const rng = makeRng((seed ^ NAME_STREAM_SALT) >>> 0);
  const deck = pool.slice();
  // Partial shuffle: only the first `take` slots need to be settled. Drawing
  // from the whole remaining deck at every step is what makes the SUBSET vary
  // with the seed and not just the order.
  for (let i = 0; i < take; i++) {
    const j = i + Math.floor(rng() * (n - i));
    const tmp = deck[i];
    deck[i] = deck[j];
    deck[j] = tmp;
  }
  return deck.slice(0, take);
}

export interface NameAssignment {
  /** The names to hand to `applyNamePool`. Possibly shorter than requested. */
  names: string[];
  /** 'pool' when at least one AI name was dealt; 'builtin' when none were. */
  source: 'pool' | 'builtin';
  /** How many cards this deal could NOT name. The honest number. */
  shortfall: number;
  /** One sentence for the interface. */
  detail: string;
}

/**
 * The call site's whole job: "name `count` drivers for seed `seed`".
 *
 * Handed a null pool it says so and returns nothing, and the caller keeps the
 * built-in names `buildField` already dealt. That is the fallback in full —
 * there is nothing else to do, because the names already exist.
 */
export function namesForField(
  pool: NamePool | null,
  seed: number,
  count: number,
): NameAssignment {
  if (!pool || pool.names.length === 0) {
    return {
      names: [],
      source: 'builtin',
      shortfall: count,
      detail:
        'No AI name pool is stored, so the built-in first-name × surname vocabulary named ' +
        'this field — the same way every field has been named until now.',
    };
  }
  const names = permuteForSeed(pool.names, seed, count);
  const shortfall = Math.max(0, count - names.length);
  return {
    names,
    source: names.length > 0 ? 'pool' : 'builtin',
    shortfall,
    detail:
      shortfall === 0
        ? `${names.length} of the ${pool.names.length} names in the pool, dealt for seed ${seed}. ` +
          'Re-seeding reshuffles this pool; it never adds to it.'
        : `The pool holds ${pool.names.length} names and this field needs ${count}. ` +
          `${names.length} drivers took a pool name; the remaining ${shortfall} kept the ` +
          'built-in one. The pool is not regenerated to cover the gap.',
  };
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/**
 * The two methods of `localStorage` this file uses, and nothing else.
 *
 * Declared as an interface so the tests and `scripts/names.ts` can run under
 * node (vitest's environment here is `node`, not `jsdom` — see `vitest.config.ts`)
 * without a DOM and without stubbing a global.
 */
export interface PoolStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The browser's store, or null off-browser.
 *
 * Wrapped in a try because merely TOUCHING `localStorage` throws in a Safari
 * private window and in a page with storage blocked by policy. A thrown getter
 * on a feature-detect is exactly the kind of thing that ends in a blank screen,
 * and this file is not allowed to have one of those.
 */
function defaultStorage(): PoolStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    // Prove it actually works rather than trusting that it exists.
    const probe = `${POOL_STORAGE_KEY}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

let storageOverride: PoolStorage | null | undefined;

/** Test seam. Pass null to simulate a browser with storage switched off. */
export function setPoolStorage(storage: PoolStorage | null | undefined): void {
  storageOverride = storage;
}

function storage(): PoolStorage | null {
  return storageOverride === undefined ? defaultStorage() : storageOverride;
}

/**
 * Read the stored pool, or null.
 *
 * REVALIDATES ON EVERY READ. The record came off a disk anybody with a devtools
 * console can write to, an older build may have written a different shape, and a
 * half-written record is a real thing. Anything that does not survive
 * `validateNameBatch` is discarded and we fall back — a corrupt pool must never
 * be able to put a real driver's name, or a fragment of JSON, on a car.
 */
export function loadPool(): NamePool | null {
  const store = storage();
  if (!store) return null;
  let text: string | null;
  try {
    text = store.getItem(POOL_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!text) return null;

  try {
    const parsed = JSON.parse(text) as Partial<NamePool>;
    if (parsed?.version !== POOL_SCHEMA_VERSION) return null;
    const { accepted } = validateNameBatch(parsed.names);
    if (accepted.length < POOL_MIN_SIZE) return null;
    return {
      version: POOL_SCHEMA_VERSION,
      names: accepted,
      generatedAt: typeof parsed.generatedAt === 'string' ? parsed.generatedAt : 'unknown',
      model: typeof parsed.model === 'string' ? parsed.model : null,
      offered: typeof parsed.offered === 'number' ? parsed.offered : accepted.length,
      rejected: Array.isArray(parsed.rejected) ? (parsed.rejected as RejectedName[]) : [],
    };
  } catch {
    return null;
  }
}

/** Write the pool. Returns false when the browser refused; never throws. */
export function savePool(pool: NamePool): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(POOL_STORAGE_KEY, JSON.stringify(pool));
    return true;
  } catch {
    return false;
  }
}

/**
 * Forget the pool.
 *
 * ⚠ EXPLICIT ONLY. Nothing in this module calls it. Requirement 2 says the pool
 * is made once, and a regeneration that happens on its own — on a version bump,
 * on a new season, on a shortfall — is the behaviour Howard asked to be removed.
 * This exists so a *person* can ask for a new set of names, from a button that
 * says what it will do.
 */
export function clearPool(): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.removeItem(POOL_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// The one call
// ---------------------------------------------------------------------------

/** How long we will wait for a batch before playing with the built-in names. */
const TIMEOUT_MS = 20_000;

export interface EnsureOptions {
  /**
   * Is there a career in progress? Requirement 4.
   *
   * PASSED IN, NOT LOOKED UP. This module deliberately does not import
   * `careerStore.ts`: only the caller knows whether the player is mid-career,
   * mid-load or on a fresh landing page, and a name pool reaching into career
   * state to answer a question the caller already knows the answer to is the
   * kind of coupling that makes both modules harder to change. Default `true`
   * — the SAFE answer, because the cost of not generating is a familiar name
   * list and the cost of generating wrongly is a renamed champion.
   */
  careerInProgress?: boolean;
  /** How many names to ask for. Defaults to `POOL_TARGET_SIZE`. */
  count?: number;
  /** Test seam. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Test seam. Defaults to `aiAccess()` — who would pay for the call. */
  access?: { payer: 'byo' | 'owner' | 'none'; headers: Record<string, string> };
}

/**
 * Get the name pool, generating it exactly once.
 *
 * The decision tree, in the order it is evaluated, is the requirement:
 *
 *   stored pool?          → return it. THE MODEL IS NOT CALLED. (steady state)
 *   career in progress?   → do not generate. Built-in names. (requirement 4)
 *   otherwise             → one call, validate, persist, return. (requirement 2)
 *
 * Every branch returns. Nothing here throws, and the worst outcome is the game
 * exactly as it behaved before this file existed.
 */
export async function ensureNamePool(options: EnsureOptions = {}): Promise<EnsuredPool> {
  const existing = loadPool();
  if (existing) {
    return {
      pool: existing,
      status: 'cached',
      detail:
        `${existing.names.length} AI-written names, generated ${existing.generatedAt}. ` +
        'Re-seeding reshuffles these; it does not write new ones.',
    };
  }

  // Requirement 4, and the default is the cautious one — see `EnsureOptions`.
  const careerInProgress = options.careerInProgress ?? true;
  if (careerInProgress) {
    return {
      pool: null,
      status: 'skipped-career-in-progress',
      detail:
        'A career is already running, so no new names were generated — renaming drivers ' +
        'underneath a league with standings and winnings would rewrite its history. The ' +
        'built-in names stand.',
    };
  }

  const count = Math.max(POOL_MIN_SIZE, Math.min(options.count ?? POOL_TARGET_SIZE, POOL_MAX_SIZE));
  const doFetch = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (!doFetch) {
    return {
      pool: null,
      status: 'unavailable',
      detail: 'No fetch is available in this environment, so the built-in names were used.',
    };
  }

  /*
    D-EZ-100 — NO KEY, NO REQUEST. A first-time visitor with no key of their
    own used to wait up to twenty seconds for this call to fail before the race
    could be named; with the owner key reserved for the owner, the answer for
    them is known before anything is sent, so the built-in names arrive
    instantly. Nothing is saved, so locking in a key later still writes a pool.
  */
  const access = options.access ?? (await aiAccess());
  if (access.payer === 'none') {
    return {
      pool: null,
      status: 'no-model',
      detail: 'The built-in names are in use. Bring your own OpenAI key to have a fresh set of names written.',
    };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let payload: { names?: unknown; model?: unknown };
  try {
    const res = await doFetch('/api/names', {
      method: 'POST',
      // D-EZ-48 — a player who brought their own key pays for their own roster
      // too; the owner's session token when it is Howard (D-EZ-100).
      headers: { 'Content-Type': 'application/json', ...access.headers },
      signal: controller.signal,
      body: JSON.stringify({ count }),
    });
    if (!res.ok) {
      // The Worker's own reason, when it gave one. It is written to be shown.
      let reason = `the name service answered ${res.status}`;
      try {
        const body = (await res.json()) as { reason?: unknown };
        if (typeof body?.reason === 'string') reason = body.reason;
      } catch {
        /* a non-JSON error body is not worth a second failure mode */
      }
      return {
        pool: null,
        status: 'unavailable',
        detail: `No AI names this time (${reason}). The built-in names were used instead.`,
      };
    }
    payload = (await res.json()) as { names?: unknown; model?: unknown };
    if (isNoModel(payload)) {
      return {
        pool: null,
        status: 'no-model',
        detail: 'The built-in names are in use. Bring your own OpenAI key to have a fresh set of names written.',
      };
    }
  } catch (e) {
    const aborted = (e as Error)?.name === 'AbortError';
    return {
      pool: null,
      status: 'unavailable',
      detail: aborted
        ? `The name service did not answer within ${TIMEOUT_MS / 1000}s. The built-in names were used.`
        : 'The name service could not be reached. The built-in names were used.',
    };
  } finally {
    clearTimeout(timer);
  }

  // Validate independently of the Worker rather than trusting its shape — the
  // same reason `narrate.ts` re-checks a line the Worker already parsed. The
  // Worker checks that the answer is a list of strings; THIS decides whether a
  // string is a fictional stock-car driver's name.
  const offered = Array.isArray(payload?.names) ? payload.names.length : 0;
  const { accepted, rejected } = validateNameBatch(payload?.names);

  if (accepted.length < POOL_MIN_SIZE) {
    return {
      pool: null,
      status: 'rejected',
      detail:
        `The model offered ${offered} names and only ${accepted.length} passed the check ` +
        `(fewer than the ${POOL_MIN_SIZE} needed to name a field with any headroom), so the ` +
        'batch was thrown away and the built-in names were used. Nothing was saved, so this ' +
        'can be tried again.',
    };
  }

  const pool: NamePool = {
    version: POOL_SCHEMA_VERSION,
    names: accepted,
    generatedAt: new Date().toISOString(),
    model: typeof payload?.model === 'string' ? payload.model : null,
    offered,
    rejected,
  };

  const saved = savePool(pool);
  return {
    pool,
    // A pool we cannot persist still names THIS session correctly; what is lost
    // is requirement 2's "once", because the next visit will ask again. Saying
    // so is the difference between a known limitation and a mystery.
    status: saved ? 'generated' : 'storage-unavailable',
    detail: saved
      ? `${accepted.length} fictional names written for this league` +
        (rejected.length > 0 ? `, ${rejected.length} rejected by the check` : '') +
        '. They will not change again unless you ask for new ones.'
      : `${accepted.length} names were written, but this browser would not store them — ` +
        'they will hold for this visit and be requested again next time.',
  };
}
